import { PortRect, SimpleLayout } from '../types';

export const GRID = 6; // px (tighter spacing)
export const PORT = { width: 18, height: 16 }; // more square ports
export const STAGE = {
  desktop1: { width: 260, height: 70 },
  desktop2: { width: 320, height: 70 },
  desktop8: { width: 420, height: 70 },
  rack16: { width: 720, height: 100 },
  rack24: { width: 900, height: 110 },
  rack48: { width: 900, height: 110 },
};

/** Visual cell size used by the layout editor modal and canvas editor-style panels. */
export const EDITOR_CELL_SIZE = 32;
export const EDITOR_CELL_GAP = 4;
export const EDITOR_NUMBER_BAND = 14;
export const EDITOR_STAGE_PAD_X = 18;
export const EDITOR_STAGE_PAD_Y = 10;
export const EDITOR_EMPTY_CELL = '#e5e7eb';
export const EDITOR_PANEL_BG = '#1a1a1a';

const LAYOUT_STEP_X = PORT.width + GRID;
const LAYOUT_STEP_Y = PORT.height + GRID;
const LAYOUT_PAD_X = 18;

const PRESET_GRIDS: Record<string, { cols: number; rows: number }> = {
  desktopTiny: { cols: 5, rows: 1 },
  desktopSmall: { cols: 8, rows: 1 },
  desktop: { cols: 12, rows: 2 },
  rack: { cols: 24, rows: 2 },
  rackLong: { cols: 24, rows: 2 },
  rackShort: { cols: 16, rows: 2 },
};

const TEMPLATE_ID_ALIASES: Record<string, string> = {
  tiny: 'desktopTiny',
  small: 'desktopSmall',
  medium: 'desktop',
  rackTall: 'rack',
  large: 'rack',
  xlarge: 'rack',
};

export interface LayoutGridCell {
  row: number;
  col: number;
  port?: PortRect;
  blank?: boolean;
}

export function parseFittedGridId(id: string | undefined | null): { cols: number; rows: number } | null {
  if (!id) return null;
  const m = /^grid:(\d+)x(\d+)$/.exec(id);
  if (!m) return null;
  const cols = Number(m[1]);
  const rows = Number(m[2]);
  if (!Number.isFinite(cols) || !Number.isFinite(rows) || cols < 1 || rows < 1) return null;
  return { cols, rows };
}

export function fittedGridTemplateId(cols: number, rows: number): string {
  for (const [id, dims] of Object.entries(PRESET_GRIDS)) {
    if (dims.cols === cols && dims.rows === rows) return id;
  }
  return `grid:${cols}x${rows}`;
}

function dimsFromTemplateSize(templateSize?: string): { cols: number; rows: number } | null {
  if (!templateSize || templateSize === 'none') return null;
  const fitted = parseFittedGridId(templateSize);
  if (fitted) return fitted;
  const id = TEMPLATE_ID_ALIASES[templateSize] || templateSize;
  if (PRESET_GRIDS[id]) return PRESET_GRIDS[id];
  if (id.startsWith('custom:')) {
    try {
      const stored = localStorage.getItem('netprobuilder-custom-templates');
      if (!stored) return null;
      const parsed = JSON.parse(stored) as Record<string, { cols?: number; rows?: number }>;
      const t = parsed[id];
      if (t && Number(t.cols) > 0 && Number(t.rows) > 0) {
        return { cols: Number(t.cols), rows: Number(t.rows) };
      }
    } catch {
      return null;
    }
  }
  return null;
}

function dimsFromEditorStage(stage: { width: number; height: number }): { cols: number; rows: number } | null {
  const cols = (stage.width - 30) / LAYOUT_STEP_X;
  const rows = (stage.height - 38) / LAYOUT_STEP_Y;
  if (
    cols >= 1 &&
    rows >= 1 &&
    Math.abs(cols - Math.round(cols)) < 0.08 &&
    Math.abs(rows - Math.round(rows)) < 0.08
  ) {
    return { cols: Math.round(cols), rows: Math.round(rows) };
  }
  return null;
}

function nearestRow(y: number, ys: number[]): number {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < ys.length; i++) {
    const dist = Math.abs(ys[i] - y);
    if (dist < bestDist) {
      best = i;
      bestDist = dist;
    }
  }
  return best;
}

/** Rebuild the editor grid (including empty cells) from a saved canvas layout. */
export function inferLayoutGrid(layout: SimpleLayout): {
  cols: number;
  rows: number;
  cells: LayoutGridCell[];
} {
  const ports = layout.ports || [];
  if (ports.length === 0) return { cols: 0, rows: 0, cells: [] };

  const named = dimsFromTemplateSize(layout.templateSize) || dimsFromEditorStage(layout.stage);
  const occupied = new Map<string, PortRect>();
  let cols = 0;
  let rows = 0;

  if (named) {
    cols = named.cols;
    rows = named.rows;
    const startY = Math.round((layout.stage.height - (rows * LAYOUT_STEP_Y - GRID)) / 2);
    const ys = Array.from(new Set(ports.map((p) => p.y))).sort((a, b) => a - b);
    for (const p of ports) {
      let col = Math.round((p.x - LAYOUT_PAD_X) / LAYOUT_STEP_X);
      let row = Math.round((p.y - startY) / LAYOUT_STEP_Y);
      if (col < 0 || col >= cols || row < 0 || row >= rows) {
        col = Math.max(0, Math.min(cols - 1, col));
        row = ys.length ? nearestRow(p.y, ys) : Math.max(0, Math.min(rows - 1, row));
      }
      occupied.set(`${row}:${col}`, p);
    }
    // If the named template didn't land most ports, pack from the occupied origin instead.
    if (occupied.size < Math.max(1, Math.floor(ports.length * 0.6))) {
      occupied.clear();
    }
  }

  if (occupied.size === 0) {
    const minX = Math.min(...ports.map((p) => p.x));
    const minY = Math.min(...ports.map((p) => p.y));
    const ys = Array.from(new Set(ports.map((p) => p.y))).sort((a, b) => a - b);
    const stepY = ys.length > 1 ? ys[1] - ys[0] : LAYOUT_STEP_Y;
    let maxCol = 0;
    let maxRow = 0;
    for (const p of ports) {
      const col = Math.max(0, Math.round((p.x - minX) / LAYOUT_STEP_X));
      const row = Math.max(0, Math.round((p.y - minY) / stepY));
      occupied.set(`${row}:${col}`, p);
      if (col > maxCol) maxCol = col;
      if (row > maxRow) maxRow = row;
    }
    cols = maxCol + 1;
    rows = maxRow + 1;
  }

  const cells: LayoutGridCell[] = [];
  const blankKeys = new Set(
    (layout.blanks || []).map((b) => `${b.row}:${b.col}`)
  );
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const port = occupied.get(`${row}:${col}`);
      cells.push({
        row,
        col,
        port,
        blank: !port && blankKeys.has(`${row}:${col}`),
      });
    }
  }

  // Named templates (e.g. desktop 12×2) are often larger than the ports. Canvas and
  // the floating panel only have those extra empty rows/cols after a port is placed
  // on them — trim so the editor opens on the same occupied bounds.
  let maxRow = -1;
  let maxCol = -1;
  for (const cell of cells) {
    if (cell.port || cell.blank) {
      if (cell.row > maxRow) maxRow = cell.row;
      if (cell.col > maxCol) maxCol = cell.col;
    }
  }
  if (maxRow >= 0 && (maxRow + 1 < rows || maxCol + 1 < cols)) {
    const nextRows = maxRow + 1;
    const nextCols = maxCol + 1;
    return {
      cols: nextCols,
      rows: nextRows,
      cells: cells.filter((cell) => cell.row < nextRows && cell.col < nextCols),
    };
  }

  return { cols, rows, cells };
}

export function templateIdFromSimpleLayout(
  layout: SimpleLayout | undefined | null,
  fallbackPortCount: number
): string {
  if (layout?.ports && layout.ports.length > 0) {
    const grid = inferLayoutGrid(layout);
    if (grid.cols > 0 && grid.rows > 0) {
      const fitted = fittedGridTemplateId(grid.cols, grid.rows);
      const saved = layout.templateSize;
      if (saved && saved !== 'none') {
        const named = dimsFromTemplateSize(saved);
        if (named && named.cols === grid.cols && named.rows === grid.rows) {
          const aliased = TEMPLATE_ID_ALIASES[saved] || saved;
          if (PRESET_GRIDS[aliased] || parseFittedGridId(saved) || String(saved).startsWith('custom:')) {
            return aliased;
          }
        }
      }
      return fitted;
    }
  }
  if (fallbackPortCount <= 5) return 'desktopTiny';
  if (fallbackPortCount <= 8) return 'desktopSmall';
  if (fallbackPortCount <= 16) return 'desktop';
  if (fallbackPortCount <= 24) return 'rack';
  return 'rackLong';
}

export function editorStageSize(cols: number, rows: number): { width: number; height: number } {
  const width = cols > 0
    ? cols * (EDITOR_CELL_SIZE + EDITOR_CELL_GAP) - EDITOR_CELL_GAP + EDITOR_STAGE_PAD_X * 2
    : 200;
  const height = rows > 0
    ? EDITOR_NUMBER_BAND * 2
      + EDITOR_STAGE_PAD_Y * 2
      + rows * EDITOR_CELL_SIZE
      + (rows - 1) * EDITOR_CELL_GAP
    : 70;
  return { width, height };
}

export function editorCellPosition(col: number, row: number): { x: number; y: number } {
  return {
    x: EDITOR_STAGE_PAD_X + col * (EDITOR_CELL_SIZE + EDITOR_CELL_GAP),
    y: EDITOR_NUMBER_BAND + EDITOR_STAGE_PAD_Y + row * (EDITOR_CELL_SIZE + EDITOR_CELL_GAP),
  };
}

export const snapToGrid = (v: number) => Math.round(v / GRID) * GRID;

/** Stable identity for a layout port. Numbers can repeat across RJ45 / SFP / custom kinds. */
export function layoutPortKey(port: { id?: string; number: number; kind?: string | null }): string {
  if (port.id) return port.id;
  const kind = port.kind != null && String(port.kind) !== '' ? String(port.kind) : 'port';
  return `p-${kind.replace(/[^a-zA-Z0-9_-]/g, '_')}-${port.number}`;
}

/** Pick the unique layout port for a number, using kind when the same number exists more than once. */
export function findLayoutPort<T extends { id?: string; number: number; kind?: string | null }>(
  ports: T[] | undefined | null,
  number: number,
  kind?: string | null
): T | undefined {
  if (!ports?.length) return undefined;
  const matches = ports.filter((p) => p.number === number);
  if (matches.length === 0) return undefined;
  if (kind != null && String(kind) !== '') {
    const byKind = matches.find((p) => String(p.kind) === String(kind));
    if (byKind) return byKind;
  }
  if (matches.length === 1) return matches[0];
  return matches.find((p) => !p.kind || String(p.kind) === 'rj45') || matches[0];
}
